import { useCallback, useState, useEffect } from 'react';
import { api } from '@ohif/core';

const customNameCache = {};
const failedCustomNameCache = new Set();
const customNamePendingRequests = {};

export const extractPlanningIdFromDescription = description => {
  if (!description || typeof description !== 'string') {
    return null;
  }

  const trimmedDescription = description.trim();

  // Prefer pure MongoDB ObjectId when present at the beginning.
  const mongoIdMatch = trimmedDescription.match(/^([a-f0-9]{24})/i);
  if (mongoIdMatch) {
    return mongoIdMatch[1];
  }

  // Extract everything before -AML/-FLX or _AML/_FLX.
  const markerMatch = trimmedDescription.match(/^(.*?)[-_](AML|FLX)(?:[-_]|$)/i);
  return markerMatch ? markerMatch[1].trim() : null;
};

export const isValidObjectId = planningId => {
  // Check if it's a valid MongoDB ObjectId (24 hexadecimal characters)
  return /^[a-f0-9]{24}$/i.test(planningId);
};

const normalizeCustomName = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  // API sometimes returns JSON-stringified plain text: "name"
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).trim();
  }

  return trimmed;
};

const fetchSingleCustomName = async planningId => {
  if (!planningId) {
    return null;
  }

  // Only fetch if it's a valid MongoDB ObjectId
  if (!isValidObjectId(planningId)) {
    return null;
  }

  if (customNameCache[planningId]) {
    return customNameCache[planningId];
  }

  if (failedCustomNameCache.has(planningId)) {
    return null;
  }

  if (customNamePendingRequests[planningId]) {
    return customNamePendingRequests[planningId];
  }

  customNamePendingRequests[planningId] = (async () => {
    try {
      const response = await api.get(`/devices/custom-name/${encodeURIComponent(planningId)}`, {
        transformResponse: value => value,
      });

      const responseData = response.data;

      const resolvedName = normalizeCustomName(responseData);

      if (resolvedName) {
        customNameCache[planningId] = resolvedName;
        failedCustomNameCache.delete(planningId);
      } else {
        failedCustomNameCache.add(planningId);
      }

      return resolvedName;
    } catch (err) {
      failedCustomNameCache.add(planningId);
      return null;
    } finally {
      delete customNamePendingRequests[planningId];
    }
  })();

  return customNamePendingRequests[planningId];
};

export const usePlanningCustomName = () => {
  const getCustomName = useCallback(fetchSingleCustomName, []);

  return { getCustomName };
};

export const precacheCustomNamesFromDescriptions = async descriptions => {
  if (!descriptions || descriptions.length === 0) {
    return {};
  }

  const uniqueDescriptions = [...new Set(descriptions)].filter(Boolean);
  const planningIds = uniqueDescriptions
    .map(desc => extractPlanningIdFromDescription(desc))
    .filter(id => typeof id === 'string' && id.length > 0);

  const validPlanningIds = [...new Set(planningIds)].filter(isValidObjectId);

  if (validPlanningIds.length === 0) {
    return {};
  }

  return fetchCustomNamesBatch(validPlanningIds);
};

const fetchCustomNamesBatch = async planningIds => {
  if (!planningIds?.length) {
    return {};
  }

  const validPlanningIds = [...new Set(planningIds)].filter(isValidObjectId);

  if (!validPlanningIds.length) {
    return {};
  }

  const pendingIds = validPlanningIds.filter(
    id => !customNameCache[String(id)] && !failedCustomNameCache.has(String(id))
  );

  if (pendingIds.length) {
    await Promise.all(pendingIds.map(id => fetchSingleCustomName(id)));
  }

  return validPlanningIds.reduce((acc, id) => {
    const cacheKey = String(id);
    if (customNameCache[cacheKey]) {
      acc[cacheKey] = customNameCache[cacheKey];
    }
    return acc;
  }, {});
};

export const usePrecacheCustomNames = descriptions => {
  const [customNamesByPlanningId, setCustomNamesByPlanningId] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!descriptions || descriptions.length === 0) {
      setCustomNamesByPlanningId({});
      setLoading(false);
      return;
    }

    let isMounted = true;

    const precacheAll = async () => {
      const uniqueDescriptions = [...new Set(descriptions)].filter(Boolean);
      const planningIds = uniqueDescriptions
        .map(desc => extractPlanningIdFromDescription(desc))
        .filter(id => typeof id === 'string' && id.length > 0);

      const validPlanningIds = [...new Set(planningIds)].filter(isValidObjectId);

      if (validPlanningIds.length === 0) {
        if (isMounted) {
          setCustomNamesByPlanningId({});
          setLoading(false);
        }
        return;
      }

      const pendingIds = validPlanningIds.filter(
        id => !customNameCache[String(id)] && !failedCustomNameCache.has(String(id))
      );

      if (pendingIds.length === 0) {
        if (isMounted) {
          const resolvedFromCache = validPlanningIds.reduce((acc, id) => {
            const cacheKey = String(id);
            if (customNameCache[cacheKey]) {
              acc[cacheKey] = customNameCache[cacheKey];
            }
            return acc;
          }, {});

          setCustomNamesByPlanningId(resolvedFromCache);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      const customNames = await fetchCustomNamesBatch(validPlanningIds);

      if (isMounted) {
        setCustomNamesByPlanningId(customNames);
        setLoading(false);
      }
    };

    precacheAll();

    return () => {
      isMounted = false;
    };
  }, [descriptions]);

  return {
    customNamesByPlanningId,
    loading,
  };
};
